import { useState, type ReactNode } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Link from '@mui/material/Link';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import CakeIcon from '@mui/icons-material/Cake';
import CelebrationIcon from '@mui/icons-material/Celebration';
import EventIcon from '@mui/icons-material/Event';
import CollectionsIcon from '@mui/icons-material/Collections';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useQuery } from '@tanstack/react-query';
import {
  LeaveRequestSchema,
  NOTICEBOARD_DAYS_AHEAD,
  NoticeboardSchema,
  OwnOccasionsSchema,
  PublicHolidaySchema,
  type AnnouncementDto,
  type LeaveRequestDto,
  type MeResponse,
  type MyTaskDto,
  type NoticeboardDto,
  type OwnOccasionsDto,
} from '@taqon/contracts';
import { Link as RouterLink } from 'react-router';
import { z } from 'zod';
import { getData, getPage } from '../../shared/api/client';
import { LIVE_QUERY } from '../../shared/ui/live';
import { businessToday, daysBetween } from '../../shared/documents/dates';
import { hasCapability } from '../../shared/session/session';
import { BRAND, COLOUR_CYCLE } from '../../shared/ui/tokens';
import { PersonAvatar } from '../../shared/ui/person';
import { dateSpan } from '../hr/AnnouncementsPage';
import { TaskCounts } from '../projects/work/MyTasksCard';
import { MyTaskList } from '../projects/work/MyTaskList';
import {
  AnnouncementDialog,
  PictureButton,
  picturesOf,
  type AnnouncementView,
} from './announcement-popup';

/**
 * The dashboard's greeting and noticeboard (CLR-63; FRD-CORE-068 to CORE-070, FRD-HRP-107): a
 * greeting for the time of day, the person's own birthday and work anniversary, and what is coming
 * up at TAQON — events, public holidays and the leave the person may already see — with a daily
 * popup for the coming week. Every part is read through the API's own permissions: a part the
 * session may not read is simply not there, and nothing new becomes visible.
 */

/** The days ahead the daily popup covers. */
export const ALERT_DAYS_AHEAD = 7;

/** `date` plus `days`, both `YYYY-MM-DD`. */
export function addDays(date: string, days: number): string {
  const [y = 0, m = 1, d = 1] = date.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

/** The hour (0–23) in the business time zone. */
export function businessHour(timeZone: string, at: Date = new Date()): number {
  const hour = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    hour: 'numeric',
    hourCycle: 'h23',
  }).format(at);
  return Number(hour) % 24;
}

export function greeting(hour: number): string {
  if (hour >= 5 && hour < 12) return 'Good morning';
  if (hour >= 12 && hour < 17) return 'Good afternoon';
  return 'Good evening';
}

/**
 * Wishes for the person themself (FRD-CORE-069): a birthday and a work anniversary, as the server
 * worked them out from their own employee record. The date of birth never reaches the browser, so
 * the birthday is wished whether or not the person may see dates of birth unmasked, and never
 * anyone else's.
 */
export function ownWishes(occasions: OwnOccasionsDto | undefined): string[] {
  if (occasions === undefined) return [];
  const wishes: string[] = [];
  if (occasions.birthdayToday) wishes.push('Happy birthday from everyone at TAQON!');
  const years = occasions.workAnniversaryYears;
  if (years !== null) {
    wishes.push(
      `Happy work anniversary: ${String(years)} year${years === 1 ? '' : 's'} with TAQON today. Thank you!`,
    );
  }
  return wishes;
}

export type ComingUpKind = 'event' | 'holiday' | 'leave';

export interface ComingUpItem {
  readonly key: string;
  readonly kind: ComingUpKind;
  readonly startDate: string;
  readonly endDate: string;
  readonly title: string;
  readonly detail: string | null;
  readonly to: string | null;
  /** An event's link for more details, on another website. */
  readonly linkUrl?: string | null;
  /** An event's pictures, in the order they were uploaded. */
  readonly pictures?: readonly string[];
  /** An event's PDF (an agenda or a brochure), when it has one. */
  readonly document?: string | null;
  /** Where an event takes place. */
  readonly location?: string | null;
  /** Whether TAQON attends the event. */
  readonly taqonAttending?: boolean;
  /** Whose leave it is, for their photo in place of the icon. */
  readonly employeeId?: string;
  readonly employeeName?: string;
}

/** An announcement's PDF, readable by everyone who may see the announcement. */
export const documentOf = (announcementId: string) =>
  `/api/v1/hr/announcements/${encodeURIComponent(announcementId)}/document`;

/**
 * Events, public holidays and approved leave as one list, soonest first. A leave the person
 * themself takes reads "Your leave"; another person's names them (the API returned it, so the
 * session may see it).
 */
export function comingUp(
  board: NoticeboardDto | undefined,
  leave: readonly LeaveRequestDto[],
  ownEmployeeId: string | undefined,
): ComingUpItem[] {
  const items: ComingUpItem[] = [
    ...(board?.events ?? []).map((e): ComingUpItem => ({
      key: `event-${e.id}`,
      kind: 'event',
      startDate: e.startDate,
      endDate: e.endDate ?? e.startDate,
      title: e.title,
      detail: e.details,
      to: null,
      linkUrl: e.linkUrl,
      pictures: picturesOf(e),
      document: e.hasDocument === true ? documentOf(e.id) : null,
      location: e.location,
      taqonAttending: e.taqonAttending,
    })),
    ...(board?.publicHolidays ?? []).map((h): ComingUpItem => ({
      key: `holiday-${h.holidayDate}-${h.name}`,
      kind: 'holiday',
      startDate: h.holidayDate,
      endDate: h.holidayDate,
      title: h.name,
      detail: 'Public holiday',
      to: null,
    })),
    ...leave.map((l): ComingUpItem => ({
      key: `leave-${l.id}`,
      kind: 'leave',
      startDate: l.startDate,
      endDate: l.endDate,
      title: l.employeeId === ownEmployeeId ? 'Your leave' : `${l.employeeName} on leave`,
      detail: l.leaveTypeName,
      to: `/hr/leave/requests/${l.id}`,
      employeeId: l.employeeId,
      employeeName: l.employeeName,
    })),
  ];
  return items.sort(
    (a, b) => a.startDate.localeCompare(b.startDate) || a.title.localeCompare(b.title),
  );
}

/** "Today", "Tomorrow", "In 3 days", or "Until 18 Oct" for something already under way. */
export function whenLabel(item: ComingUpItem, today: string): string {
  if (item.startDate <= today) {
    return item.endDate > today ? `Until ${dateSpan(item.endDate, null)}` : 'Today';
  }
  const days = daysBetween(today, item.startDate);
  return days === 1 ? 'Tomorrow' : `In ${String(days)} days`;
}

/** Everything the dashboard's noticeboard reads, each only when the session may read it. */
export function useNoticeboard(me: MeResponse) {
  const canBoard = hasCapability(me, 'hr.announcement:view');
  const canLeave = hasCapability(me, 'hr.leave_request:view');
  const canOwnRecord = hasCapability(me, 'hr.employee:view');
  // Public holidays come with the noticeboard; a session that may see holidays but not the
  // noticeboard reads them from the holiday list, so a holiday is never hidden by that gap.
  const canHolidays = hasCapability(me, 'hr.public_holiday:view');
  const board = useQuery({
    queryKey: ['hr', 'noticeboard'],
    enabled: canBoard,
    retry: false,
    queryFn: () => getData('/api/v1/hr/noticeboard', NoticeboardSchema),
    ...LIVE_QUERY,
  });
  const today = board.data?.today ?? businessToday(me.businessTimezone);
  const until = board.data?.until ?? addDays(today, NOTICEBOARD_DAYS_AHEAD);
  const leave = useQuery({
    queryKey: ['hr', 'noticeboard', 'leave', today],
    enabled: canLeave,
    retry: false,
    queryFn: () =>
      getPage(
        `/api/v1/hr/leave-requests?pageSize=50&sort=start_date&filter[status]=approved&filter[from]=${today}&filter[to]=${until}`,
        LeaveRequestSchema,
      ),
    ...LIVE_QUERY,
  });
  const holidays = useQuery({
    queryKey: ['hr', 'noticeboard', 'holidays', today],
    enabled: !canBoard && canHolidays,
    retry: false,
    queryFn: async () => {
      const years = [...new Set([today.slice(0, 4), until.slice(0, 4)])];
      const lists = await Promise.all(
        years.map((year) =>
          getData(`/api/v1/hr/public-holidays?year=${year}`, z.array(PublicHolidaySchema)),
        ),
      );
      return lists
        .flat()
        .filter((h) => h.status === 'active' && h.holidayDate >= today && h.holidayDate <= until)
        .map((h) => ({ holidayDate: h.holidayDate, name: h.name }));
    },
    ...LIVE_QUERY,
  });
  const own = useQuery({
    queryKey: ['hr', 'employee', 'me', 'occasions'],
    enabled: canOwnRecord,
    retry: false,
    queryFn: () => getData('/api/v1/hr/employees/me/occasions', OwnOccasionsSchema),
  });
  const source: NoticeboardDto | undefined =
    board.data ??
    (holidays.data === undefined
      ? undefined
      : { today, until, events: [], news: [], publicHolidays: holidays.data });
  const items = comingUp(source, leave.data?.items ?? [], own.data?.employeeId ?? undefined);
  return {
    today,
    items,
    news: board.data?.news ?? [],
    wishes: ownWishes(own.data),
    loading:
      (canBoard && board.isPending) ||
      (!canBoard && canHolidays && holidays.isPending) ||
      (canLeave && leave.isPending),
    shown: canBoard || canLeave || canHolidays,
  };
}

/** The darker shade of a colour of the cycle: icons and text on a light tint keep 3:1 or better. */
const cycleDark = (index: number) => COLOUR_CYCLE[index]?.[1] ?? BRAND.ink;
const cycleLight = (index: number) => COLOUR_CYCLE[index]?.[0] ?? BRAND.mint;

const KIND_STYLE: Record<ComingUpKind, { icon: ReactNode; colour: string; label: string }> = {
  event: { icon: <CelebrationIcon />, colour: cycleDark(0), label: 'Event' },
  holiday: { icon: <EventIcon />, colour: cycleDark(2), label: 'Holiday' },
  leave: { icon: <BeachAccessIcon />, colour: cycleDark(4), label: 'Leave' },
};

/** What the popup shows of an event of the list. */
function eventView(item: ComingUpItem, today: string): AnnouncementView {
  return {
    title: item.title,
    kindLabel: KIND_STYLE[item.kind].label,
    startDate: item.startDate,
    endDate: item.endDate,
    when: whenLabel(item, today),
    location: item.location ?? null,
    taqonAttending: item.taqonAttending ?? false,
    details: item.detail,
    linkUrl: item.linkUrl ?? null,
    document: item.document ?? null,
    pictures: item.pictures ?? [],
  };
}

function ComingUpList({
  items,
  today,
  large = false,
}: {
  items: readonly ComingUpItem[];
  today: string;
  large?: boolean;
}) {
  const [opened, setOpened] = useState<ComingUpItem | null>(null);
  return (
    <List dense disablePadding>
      <AnnouncementDialog
        view={opened === null ? null : eventView(opened, today)}
        onClose={() => {
          setOpened(null);
        }}
      />
      {items.map((item) => {
        const style = KIND_STYLE[item.kind];
        const pictures = item.pictures ?? [];
        const open = () => {
          setOpened(item);
        };
        return (
          <ListItem key={item.key} disableGutters sx={{ alignItems: 'flex-start', gap: 1.5 }}>
            {item.kind === 'leave' && item.employeeName !== undefined ? (
              <Box sx={{ mt: 0.5, display: 'flex' }}>
                <PersonAvatar
                  name={item.employeeName}
                  employeeId={item.employeeId ?? null}
                  size={34}
                />
              </Box>
            ) : (
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mt: 0.5,
                  width: 34,
                  height: 34,
                  borderRadius: 2,
                  display: 'grid',
                  placeItems: 'center',
                  color: style.colour,
                  bgcolor: alpha(style.colour, 0.12),
                  '& svg': { fontSize: 20 },
                }}
                aria-hidden
              >
                {style.icon}
              </ListItemIcon>
            )}
            <ListItemText
              primary={
                item.kind === 'event' ? (
                  // An event opens in the popup, with its pictures and everything it says.
                  <Link
                    component="button"
                    type="button"
                    onClick={open}
                    sx={{ fontWeight: 600, textAlign: 'left', verticalAlign: 'baseline' }}
                  >
                    {item.title}
                  </Link>
                ) : item.to === null ? (
                  item.title
                ) : (
                  <Link component={RouterLink} to={item.to} sx={{ fontWeight: 600 }}>
                    {item.title}
                  </Link>
                )
              }
              secondary={
                <>
                  {[
                    dateSpan(item.startDate, item.endDate),
                    whenLabel(item, today),
                    item.location ?? null,
                    item.detail,
                  ]
                    .filter((part) => part !== null && part !== '')
                    .join(' · ')}
                  {item.linkUrl != null && (
                    <>
                      {' '}
                      <Link
                        href={item.linkUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}
                      >
                        More details
                        <OpenInNewIcon
                          fontSize="inherit"
                          aria-label="(opens another website)"
                          sx={{ ml: 0.25, verticalAlign: 'middle' }}
                        />
                      </Link>
                    </>
                  )}
                  {item.document != null && (
                    <>
                      {' '}
                      <Link
                        href={item.document}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}
                      >
                        Open PDF
                        <OpenInNewIcon
                          fontSize="inherit"
                          aria-label="(opens in a new tab)"
                          sx={{ ml: 0.25, verticalAlign: 'middle' }}
                        />
                      </Link>
                    </>
                  )}
                  {large && pictures.length > 0 && (
                    <PictureButton
                      pictures={pictures}
                      title={item.title}
                      onOpen={open}
                      sx={{ display: 'block', width: '100%', height: 200, borderRadius: 2, mt: 1 }}
                    />
                  )}
                </>
              }
              slotProps={{
                primary: { sx: { fontWeight: 600 } },
                secondary: { sx: { overflowWrap: 'anywhere' } },
              }}
            />
            {!large && pictures.length > 0 && (
              <PictureButton
                pictures={pictures}
                title={item.title}
                onOpen={open}
                sx={{ width: 72, height: 52, borderRadius: 1.5, mt: 0.5 }}
              />
            )}
            <Stack spacing={0.5} sx={{ alignItems: 'flex-end', flexShrink: 0 }}>
              <Chip
                size="small"
                label={style.label}
                sx={{ mt: 0.75, bgcolor: alpha(style.colour, 0.12), color: 'text.primary' }}
              />
              {item.taqonAttending === true && (
                <Chip size="small" color="success" variant="outlined" label="TAQON attending" />
              )}
            </Stack>
          </ListItem>
        );
      })}
    </List>
  );
}

/** The person's own wishes, under the greeting, on the days there are any. */
export function Wishes({ wishes }: { wishes: readonly string[] }) {
  if (wishes.length === 0) return null;
  return (
    <Paper
      role="status"
      sx={{
        p: { xs: 2, sm: 2.5 },
        display: 'flex',
        gap: 2,
        alignItems: 'center',
        background: `linear-gradient(120deg, ${alpha(BRAND.mint, 0.22)}, ${alpha(cycleLight(5), 0.12)} 60%, ${alpha(cycleLight(3), 0.12)})`,
      }}
    >
      <Box
        aria-hidden
        sx={{
          width: 48,
          height: 48,
          borderRadius: '50%',
          display: 'grid',
          placeItems: 'center',
          bgcolor: BRAND.charcoal,
          color: BRAND.mint,
          flexShrink: 0,
        }}
      >
        <CakeIcon />
      </Box>
      <Stack spacing={0.5} sx={{ minWidth: 0 }}>
        {wishes.map((wish) => (
          <Typography key={wish} variant="subtitle1" sx={{ fontWeight: 700 }}>
            {wish}
          </Typography>
        ))}
      </Stack>
    </Paper>
  );
}

/** The "Coming up" card: the next 30 days. */
export function ComingUpCard({
  items,
  today,
  loading,
}: {
  items: readonly ComingUpItem[];
  today: string;
  loading: boolean;
}) {
  return (
    <Paper sx={{ p: 2.5, height: '100%' }} component="section" aria-label="Coming up">
      <Typography variant="h6" component="h2" sx={{ mb: 1.5 }}>
        Coming up
      </Typography>
      {loading ? (
        <Typography variant="body2" color="text.secondary">
          Loading…
        </Typography>
      ) : items.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Nothing announced for the next {NOTICEBOARD_DAYS_AHEAD} days.
        </Typography>
      ) : (
        <ComingUpList items={items} today={today} />
      )}
    </Paper>
  );
}

/**
 * The news posted by HR or an administrator, in a box like "Coming up" beside it: the same frame,
 * the same heading, the same height.
 */
export function NewsSection({ news }: { me: MeResponse; news: readonly AnnouncementDto[] }) {
  if (news.length === 0) return null;
  return (
    <Paper
      component="section"
      aria-label="TAQON news"
      sx={{ p: 2.5, height: '100%', display: 'flex', flexDirection: 'column' }}
    >
      <Typography variant="h6" component="h2" sx={{ mb: 1.5 }}>
        News
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          flexGrow: 1,
          // One story fills the box; more share it.
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 240px), 1fr))',
          alignContent: 'start',
        }}
      >
        {news.map((item) => (
          <NewsCard key={item.id} item={item} />
        ))}
      </Box>
    </Paper>
  );
}

/**
 * A news picture keeps a photo's 16:9 shape whatever the card's width, so a single story filling
 * the News box is not cropped into a wide strip; the cap stops it growing tall on wide screens.
 */
const THUMBNAIL_FRAME = { width: '100%', aspectRatio: '16 / 9', maxHeight: 300 } as const;

function NewsThumbnail({ item }: { item: AnnouncementDto }) {
  // The announcement's own first picture, read with the announcement permission (CLR-63); a
  // picture that cannot be loaded (storage unavailable, say) shows the placeholder too.
  const pictures = picturesOf(item);
  const [failed, setFailed] = useState(false);
  const first = pictures[0];
  if (first === undefined || failed) {
    return (
      <Box
        aria-hidden
        sx={{
          ...THUMBNAIL_FRAME,
          display: 'grid',
          placeItems: 'center',
          color: BRAND.mint,
          background: `linear-gradient(135deg, ${BRAND.charcoal}, ${alpha(BRAND.charcoal, 0.85)})`,
          '& svg': { fontSize: 48 },
        }}
      >
        <NewspaperIcon />
      </Box>
    );
  }
  return (
    <Box sx={{ position: 'relative' }}>
      <Box
        component="img"
        src={first}
        alt=""
        loading="lazy"
        onError={() => {
          setFailed(true);
        }}
        sx={{ ...THUMBNAIL_FRAME, display: 'block', objectFit: 'cover' }}
      />
      {pictures.length > 1 && (
        <Chip
          size="small"
          icon={<CollectionsIcon />}
          label={`${String(pictures.length)} pictures`}
          sx={{
            position: 'absolute',
            right: 8,
            bottom: 8,
            color: '#fff',
            bgcolor: alpha(BRAND.charcoal, 0.75),
            '& .MuiChip-icon': { color: '#fff' },
          }}
        />
      )}
    </Box>
  );
}

/** What the popup shows of a news item. */
function newsView(item: AnnouncementDto): AnnouncementView {
  return {
    title: item.title,
    kindLabel: 'News',
    startDate: item.startDate,
    endDate: null,
    details: item.details,
    linkUrl: item.linkUrl,
    document: item.hasDocument === true ? documentOf(item.id) : null,
    pictures: picturesOf(item),
  };
}

/** A news card opens the news in the popup: its pictures, its full text and its links. */
function NewsCard({ item }: { item: AnnouncementDto }) {
  const [open, setOpen] = useState(false);
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardActionArea
        onClick={() => {
          setOpen(true);
        }}
        aria-label={`Open ${item.title}`}
        // From the top: a button centres its content, which pushed the shorter card's picture down.
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          justifyContent: 'flex-start',
        }}
      >
        <NewsThumbnail item={item} />
        <Box sx={{ p: 2 }}>
          <Typography variant="caption" color="text.secondary">
            {dateSpan(item.startDate, null)}
          </Typography>
          <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
            {item.title}
          </Typography>
          {item.details !== null && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 0.5,
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {item.details}
            </Typography>
          )}
        </Box>
      </CardActionArea>
      <AnnouncementDialog
        view={open ? newsView(item) : null}
        onClose={() => {
          setOpen(false);
        }}
      />
    </Card>
  );
}

const ALERT_KEY = 'ibms.dashboard.upcoming-alert';

function dismissedOn(userId: string): string | null {
  try {
    return window.localStorage.getItem(`${ALERT_KEY}.${userId}`);
  } catch {
    return null;
  }
}

function dismiss(userId: string, today: string): void {
  try {
    window.localStorage.setItem(`${ALERT_KEY}.${userId}`, today);
  } catch {
    // Without storage the popup simply shows again next time.
  }
}

/** What the daily popup lists: under way or starting within the coming week. */
export function thisWeek(items: readonly ComingUpItem[], today: string): ComingUpItem[] {
  const last = addDays(today, ALERT_DAYS_AHEAD);
  return items.filter((item) => item.startDate <= last && item.endDate >= today);
}

/**
 * The popup on the first visit of the business day (FRD-CORE-070): the person's wishes, their own
 * open tasks with the time remaining (CLR-66), and what is under way or coming in the next seven
 * days. Closing it keeps it closed until tomorrow, in this browser; the "Coming up" and "My tasks"
 * cards always show the same.
 */
export function DailyAlert({
  me,
  items,
  wishes,
  tasks = [],
  today,
  ready,
}: {
  me: MeResponse;
  items: readonly ComingUpItem[];
  wishes: readonly string[];
  /** The caller's open assigned tasks; empty when they use no project work. */
  tasks?: readonly MyTaskDto[];
  today: string;
  ready: boolean;
}) {
  const [closed, setClosed] = useState(() => dismissedOn(me.userId) === today);
  const week = thisWeek(items, today);
  const open = ready && !closed && (week.length > 0 || wishes.length > 0 || tasks.length > 0);
  const close = () => {
    dismiss(me.userId, today);
    setClosed(true);
  };
  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="sm" aria-labelledby="daily-alert-title">
      <DialogTitle id="daily-alert-title">
        {greeting(businessHour(me.businessTimezone))}, {me.displayName}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          {wishes.map((wish) => (
            <Typography
              key={wish}
              sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600 }}
            >
              <CakeIcon color="primary" aria-hidden /> {wish}
            </Typography>
          ))}
          {tasks.length > 0 && (
            <Box component="section" aria-label="Your tasks">
              <Stack
                direction="row"
                sx={{ alignItems: 'baseline', justifyContent: 'space-between', mb: 1 }}
              >
                <Typography variant="subtitle2" color="text.secondary">
                  Your tasks
                </Typography>
                <Link component={RouterLink} to="/my-tasks" onClick={close} variant="body2">
                  Open My tasks
                </Link>
              </Stack>
              <Stack spacing={1.5}>
                <TaskCounts tasks={tasks} today={today} />
                <MyTaskList tasks={tasks} today={today} limit={3} onOpen={close} />
              </Stack>
            </Box>
          )}
          {week.length > 0 && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                Coming up in the next {ALERT_DAYS_AHEAD} days
              </Typography>
              <ComingUpList items={week} today={today} large />
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={close}>
          Got it
        </Button>
      </DialogActions>
    </Dialog>
  );
}
